/**
 * Korean display formats shared by confirm, records, calendar and share cards.
 * Every timestamp is shown in KST regardless of the device time zone so a
 * record saved abroad still reads the same as the receipt.
 */
const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"] as const;

export function formatWon(value: number): string {
	return `${Math.round(value).toLocaleString("ko-KR")}원`;
}

interface KstParts {
	year: number;
	month: number;
	day: number;
	hour: number;
	minute: number;
	weekday: number;
}

export function kstParts(iso: string | Date): KstParts | null {
	const time = typeof iso === "string" ? Date.parse(iso) : iso.getTime();
	if (!Number.isFinite(time)) return null;
	const shifted = new Date(time + 9 * 60 * 60 * 1000);
	return {
		year: shifted.getUTCFullYear(),
		month: shifted.getUTCMonth() + 1,
		day: shifted.getUTCDate(),
		hour: shifted.getUTCHours(),
		minute: shifted.getUTCMinutes(),
		weekday: shifted.getUTCDay(),
	};
}

/** "2026-10-16" in KST. */
export function kstDateKey(iso: string | Date): string {
	const parts = kstParts(iso);
	if (!parts) return "";
	return `${parts.year}-${String(parts.month).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`;
}

/** "10월 16일 (금)" — the year is added only when it is not the current year. */
export function formatKstDate(iso: string | Date, now = new Date()): string {
	const parts = kstParts(iso);
	if (!parts) return "날짜 확인 필요";
	const current = kstParts(now);
	const year = current && current.year !== parts.year ? `${parts.year}년 ` : "";
	return `${year}${parts.month}월 ${parts.day}일 (${WEEKDAYS[parts.weekday]})`;
}

/** "10월 16일 (금) 오전 8:42" */
export function formatKstDateTime(
	iso: string | Date,
	now = new Date(),
): string {
	const parts = kstParts(iso);
	if (!parts) return "날짜 확인 필요";
	const meridiem = parts.hour < 12 ? "오전" : "오후";
	const hour12 = parts.hour % 12 === 0 ? 12 : parts.hour % 12;
	return `${formatKstDate(iso, now)} ${meridiem} ${hour12}:${String(parts.minute).padStart(2, "0")}`;
}
